import { SalesType } from '../../generated/api/salesType.js';
import { fetchCampaigns } from '../fetch.js';
import type { CampaignContext } from '../../generated/api/campaignContext.js';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion.js';

export interface VoucherCampaignData {
  campaignContexts: CampaignContext[];
  campaignPromotions: CampaignPromotion[];
}

/**
 * Fetch campaign contexts and promotions for a voucher across multiple model codes.
 * Deduplicates results by context key and promotion ID.
 */
export const fetchVoucherCampaigns = async (
  voucherCode: string,
  onlineModelCodes: string[]
): Promise<VoucherCampaignData> => {
  const uniqueCodes = [...new Set(onlineModelCodes)];
  const results = await Promise.all(
    uniqueCodes.map(onlineModel =>
      fetchCampaigns({ salesType: SalesType.NEW_SALE, voucher: voucherCode, onlineModel }).catch(() => null)
    )
  );

  const campaignContexts: CampaignContext[] = [];
  const campaignPromotions: CampaignPromotion[] = [];
  const seenContextKeys = new Set<string>();
  const seenPromoIds = new Set<string>();

  for (const result of results.filter(Boolean)) {
    for (const ctx of result?.campaignContexts ?? []) {
      const key = `${ctx.offerCode}-${ctx.commercialProductCode}-${ctx.addOnCode}`;
      if (!seenContextKeys.has(key)) {
        seenContextKeys.add(key);
        campaignContexts.push(ctx);
      }
    }
    for (const promo of result?.campaigns ?? []) {
      if (!seenPromoIds.has(promo.campaignPromotionId)) {
        seenPromoIds.add(promo.campaignPromotionId);
        campaignPromotions.push(promo);
      }
    }
  }

  return { campaignContexts, campaignPromotions };
};
